/**
 * PlaygroundSidebar Component
 *
 * Left sidebar for playground session history:
 * - Search input for filtering sessions by message content
 * - Chronological list (newest first)
 * - Truncated user message display (20 chars + "...")
 * - Active session highlighting
 * - Click to load session details
 *
 * Refactored to use session_id and user_message fields.
 */

import React, { useState, useRef, useEffect } from 'react';
import { PlaygroundSessionListItem } from '../../types/playground';

interface PlaygroundSidebarProps {
  sessions: PlaygroundSessionListItem[];
  activeSessionId: string | null;
  isNewSession: boolean;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onSessionSelect: (sessionId: string) => void;
  onNewSession: () => void;
  onToggleSidebar: () => void;
  onSearchFocus?: () => void;
  onRenameSession?: (sessionId: string, newName: string) => void;
  onDeleteSession?: (sessionId: string) => void;
  isLoading?: boolean;
}

export const PlaygroundSidebar: React.FC<PlaygroundSidebarProps> = ({
  sessions,
  activeSessionId,
  isNewSession,
  searchQuery,
  onSearchChange,
  onSessionSelect,
  onNewSession,
  onToggleSidebar,
  onSearchFocus,
  onRenameSession,
  onDeleteSession,
  isLoading = false,
}) => {
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const [menuOpenForSession, setMenuOpenForSession] = useState<string | null>(null);
  const [renamingSession, setRenamingSession] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState<string>('');
  const menuRef = useRef<HTMLDivElement>(null);

  // Focus search when onSearchFocus changes to true
  React.useEffect(() => {
    if (onSearchFocus && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [onSearchFocus]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpenForSession(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMenuClick = (e: React.MouseEvent, sessionId: string) => {
    e.stopPropagation();
    setMenuOpenForSession(menuOpenForSession === sessionId ? null : sessionId);
  };

  const handleRename = (sessionId: string, session: PlaygroundSessionListItem) => {
    setRenamingSession(sessionId);
    // Use custom_name if it exists, otherwise use user_message (without truncation)
    setRenameValue(session.custom_name || session.user_message);
    setMenuOpenForSession(null);
  };

  const handleRenameSubmit = (sessionId: string) => {
    if (renameValue.trim() && onRenameSession) {
      onRenameSession(sessionId, renameValue.trim());
    }
    setRenamingSession(null);
    setRenameValue('');
  };

  const handleDelete = (sessionId: string) => {
    if (onDeleteSession && window.confirm('Are you sure you want to delete this session?')) {
      onDeleteSession(sessionId);
    }
    setMenuOpenForSession(null);
  };

  const truncateMessage = (message: string, maxLength: number = 26): string => {
    if (message.length <= maxLength) {
      return message;
    }
    return message.substring(0, maxLength) + '...';
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getStatusDotColor = (status: string): string => {
    switch (status) {
      case 'completed':
        return 'bg-green-500';
      case 'running':
        return 'bg-blue-500';
      case 'failed':
        return 'bg-red-500';
      case 'pending':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <div className="w-64 bg-white flex flex-col h-full overflow-hidden">
      {/* Header: Toggle and New Session Button - Docked */}
      <div className="flex-shrink-0 p-3 bg-white space-y-2">
        {/* Row 1: Toggle Button + New Session Button */}
        <div className="flex gap-2 items-center">
          {/* Sidebar Toggle Button */}
          <button
            onClick={onToggleSidebar}
            className="p-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 flex-shrink-0"
            aria-label="Hide sidebar"
            title="Hide sidebar"
          >
            <svg
              className="w-4 h-4 text-gray-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            </svg>
          </button>

          {/* New Session Button */}
          <button
            onClick={onNewSession}
            className={`
              flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors flex items-center justify-center gap-2
              ${isNewSession
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }
            `}
            aria-label="Create new session"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Session
          </button>
        </div>

        {/* Row 2: Search Input (Full Width) */}
        <div className="relative">
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search sessions..."
            className="w-full px-3 py-2 pl-9 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            aria-label="Search playground sessions"
          />
          <svg
            className="absolute left-3 top-2.5 h-4 w-4 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>

      {/* Session History List - Scrollable */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {isLoading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" aria-label="Loading sessions"></div>
          </div>
        ) : sessions.length === 0 ? (
          <div className="p-4 text-center text-sm text-gray-500">
            {searchQuery ? 'No sessions found' : 'No sessions yet'}
          </div>
        ) : (
          <div className="py-2">
            {sessions.map((session) => (
              <div
                key={session.session_id}
                className={`
                  group relative w-full text-left hover:bg-gray-100 transition-colors border-l-4
                  ${activeSessionId === session.session_id
                    ? 'bg-blue-50 border-l-blue-500'
                    : 'border-l-transparent'
                  }
                `}
              >
                {renamingSession === session.session_id ? (
                  // Rename Input
                  <div className="px-4 py-3">
                    <input
                      type="text"
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleRenameSubmit(session.session_id);
                        } else if (e.key === 'Escape') {
                          setRenamingSession(null);
                        }
                      }}
                      onBlur={() => handleRenameSubmit(session.session_id)}
                      autoFocus
                      className="w-full px-2 py-1 text-sm border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                ) : (
                  <button
                    onClick={() => onSessionSelect(session.session_id)}
                    className="w-full px-4 py-2.5 text-left relative"
                    aria-current={activeSessionId === session.session_id ? 'true' : undefined}
                  >
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-900 truncate flex-1">
                        {session.custom_name || truncateMessage(session.user_message)}
                      </p>

                      {/* Status Dot - Hide on hover */}
                      <div
                        className={`w-2 h-2 rounded-full flex-shrink-0 group-hover:opacity-0 transition-opacity ${getStatusDotColor(session.status)} ${
                          session.status === 'running' ? 'animate-pulse' : ''
                        }`}
                        title={session.status.charAt(0).toUpperCase() + session.status.slice(1)}
                        aria-label={`Status: ${session.status}`}
                      />

                      {/* Three-dot Menu Button - Show on hover (replaces status dot position) */}
                      {renamingSession !== session.session_id && (
                        <button
                          onClick={(e) => handleMenuClick(e, session.session_id)}
                          className="absolute top-2 right-2 p-1 hover:bg-gray-200 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                          aria-label="Session menu"
                        >
                          <svg className="w-3.5 h-3.5 text-gray-600" fill="currentColor" viewBox="0 0 16 16">
                            <circle cx="2" cy="8" r="1.5"/>
                            <circle cx="8" cy="8" r="1.5"/>
                            <circle cx="14" cy="8" r="1.5"/>
                          </svg>
                        </button>
                      )}
                    </div>
                  </button>
                )}

                {/* Dropdown Menu - Positioned absolutely outside button */}
                {renamingSession !== session.session_id && menuOpenForSession === session.session_id && (
                  <div className="absolute top-2 right-2 z-20">
                    <div
                      ref={menuRef}
                      className="absolute right-0 mt-6 w-40 bg-white border border-gray-200 rounded-md shadow-lg"
                    >
                      <button
                        onClick={() => handleRename(session.session_id, session)}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Rename
                      </button>
                      <button
                        onClick={() => handleDelete(session.session_id)}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer Info - Docked at Bottom */}
      <div className="flex-shrink-0 p-3 bg-white">
        <p className="text-xs text-gray-500 text-center">
          {sessions.length} {sessions.length === 1 ? 'session' : 'sessions'} total
        </p>
      </div>
    </div>
  );
};
